import {useTheme} from '@mui/material';
import {useMediaQueries, useQueryParams} from 'presentation/pages';
import {UseHealthDuplicityDialogProps} from './types';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {
	FeaturesEnum,
	HomeEnum,
	QueryParamsEnum,
	TelehealthBookingEnum,
} from 'presentation/constants';
import {useGetHealthDistrictsQuery} from '../../../../../HealthDistrictSelection/queries/useGetHealthDistrictsQuery';
import {TypeLocalNameEnum} from 'domain/usecases';
import {GetHealthDistrictsParams} from 'domain/usecases/private/booking-flow/health/get-health-districts';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useNavigate} from 'react-router-dom';
import {TELEMEDICINE_INFO} from 'presentation/modules/booking/shared/constants/telemedicineInfo';
import {translations} from 'presentation/translations-new';
import {useState} from 'react';
import {ONLY_TELEMEDICINE_SPECIALTIES_CODE} from 'presentation/modules/booking/shared/constants/onlyTelemedicineSpecialties';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';

const {goBack, goToHome} = translations['pt-br'].commons;
const {
	headerTitle: duplicityHeaderTitle,
	teleHeaderTitle,
	teleDescription,
	description: duplicityDescription,
	proceedWithTelehealth,
} = translations['pt-br'].privateFlow.bookingFlow.duplicityWarning;

function useHealthDuplicityDialog({
	scheduledBooking,
	setOpen,
	isScheduleScript,
	swapSpecialtyCallback,
	isAuthorized,
}: UseHealthDuplicityDialogProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const [actionSelected, setActionSelected] = useState<'primary' | 'secondary'>('primary');
	const {isMobile} = useMediaQueries();
	const {colors} = useTheme();
	const {setSearchParams} = useQueryParams();
	const navigate = useNavigate();
	const {isEnabled: isOnlyTelemedicineSpecialtyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SPECIALTY_ONLY_TELEMEDICINE_CHIP,
	);

	const bookingTypeIsTelemedicine = bookingData.bookingType === BookingTypeEnum.TELEHEALTH;
	const scheduledBookingIsTelemedicine =
		scheduledBooking.bookingType === BookingTypeEnum.TELEHEALTH;
	const shouldUseTelemedicineFlow = bookingTypeIsTelemedicine || scheduledBookingIsTelemedicine;
	const shouldRedirectToTelemedicineFlow =
		ONLY_TELEMEDICINE_SPECIALTIES_CODE.includes(bookingData.specialtyCode) &&
		isOnlyTelemedicineSpecialtyEnabled;

	const districtParams: GetHealthDistrictsParams = {
		stateCode: (bookingData.ufCode ?? '').toUpperCase(),
		locationName: (bookingData.cityName ?? '').toUpperCase(),
		locationType:
			bookingData.typeLocalName === TypeLocalNameEnum.COORDENADAS
				? TypeLocalNameEnum.CIDADE
				: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
		specialtyCode: Number(bookingData.specialtyCode ?? 0),
	};

	const {isFetching: isDistrictListFetching, refetch: refetchDistrictList} =
		useGetHealthDistrictsQuery({
			params: districtParams,
			enabled: false,
		});

	const shouldProceedWithTelehealth =
		scheduledBooking?.flExecutaSessao &&
		bookingData?.isFavoriteDoctorsFlow &&
		isAuthorized === true;

	const getSecondaryText = () => {
		if (shouldProceedWithTelehealth) {
			return proceedWithTelehealth;
		}
		if (isScheduleScript) {
			return goToHome;
		}
		return goBack;
	};

	const disabledSwapSpecialtyButton = isDistrictListFetching && bookingData.isManualLocation;

	const contentTitle = scheduledBookingIsTelemedicine ? teleHeaderTitle : duplicityHeaderTitle;
	const contentDescription = scheduledBookingIsTelemedicine
		? teleDescription
		: duplicityDescription;

	const handleSwapSpecialty = async ({isRebooking = true}: {isRebooking?: boolean}) => {
		if (!isRebooking && shouldProceedWithTelehealth) {
			setActionSelected('secondary');
		}

		if (shouldRedirectToTelemedicineFlow) {
			const {PROFESSIONAL_STEP: TELEHEALTH_PROFISSIONAL_STEP} = getTelehealthStepPosition();
			setBookingData(prev => ({
				...prev,
				unitCode: TELEMEDICINE_INFO.code,
				unitName: TELEMEDICINE_INFO.name,
				unitAddress: '',
				ufCode: TELEMEDICINE_INFO.ufCode,
				ufName: TELEMEDICINE_INFO.ufName,
				cityName: TELEMEDICINE_INFO.cityName,
				bookingRedirect: true,
				bookingType: BookingTypeEnum.TELEHEALTH,
				hourAt: '',
				dateAt: '',
				professionalCode: '',
				professionalName: '',
				isRebooking,
				bookingNumber: scheduledBooking.bookingNumber,
				typeLocalName: TypeLocalNameEnum.CIDADE,
				isManualLocation: true,
				initialBookingLocationData: {
					coordinates: prev.coordinates,
					ufCode: prev.ufCode,
					ufName: prev.ufName,
					cityName: prev.cityName,
					typeLocalName: prev.typeLocalName || TypeLocalNameEnum.CIDADE,
					bookingType: BookingTypeEnum.HEALTH,
				},
			}));
			setActiveBookingFlowStep(TELEHEALTH_PROFISSIONAL_STEP);
			navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
			setSearchParams(prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				return prev;
			});

			return;
		}

		const isReplaceAction = isRebooking && scheduledBookingIsTelemedicine;

		const getFlowBasedValue = <T, P>(arg1: T, arg2: P) => {
			return shouldUseTelemedicineFlow ? arg1 : arg2;
		};

		const flowType = isReplaceAction
			? FlowEnum.HEALTH
			: getFlowBasedValue(FlowEnum.TELEHEALTH, FlowEnum.HEALTH);

		const {DISTRICT_STEP, UNIT_STEP, PROFESSIONAL_STEP} = getStepPosition({
			flowType,
			isManualLocation: bookingData.isManualLocation,
		});

		let areDistrictsEmpty = false;
		if (bookingData.isManualLocation && !shouldUseTelemedicineFlow) {
			const {data: districtList, error: districtListError} = await refetchDistrictList();

			const districtListHasUnprocessableError = isUnprocessableError(districtListError);
			const districtListEmpty = districtList && districtList.length === 0;
			areDistrictsEmpty = Boolean(districtListHasUnprocessableError || districtListEmpty);
		}

		const noDistrictsAndHasTelehealthAndIsManualLocation =
			areDistrictsEmpty && bookingData.hasTelehealth && bookingData.isManualLocation;

		const bookingStepToRedirect = () => {
			if (isReplaceAction) return UNIT_STEP;
			if (shouldUseTelemedicineFlow) return PROFESSIONAL_STEP;
			if (noDistrictsAndHasTelehealthAndIsManualLocation) return UNIT_STEP;

			return bookingData.isManualLocation ? DISTRICT_STEP : UNIT_STEP;
		};

		setActiveBookingFlowStep(bookingStepToRedirect());

		setBookingData(prev => ({
			...prev,
			specialtyCode: scheduledBooking.specialtyCode,
			subSpecialtyCode:
				!scheduledBooking.subSpecialtyCode || scheduledBooking.subSpecialtyCode === '0'
					? ''
					: scheduledBooking.subSpecialtyCode,
			specialtyName: scheduledBooking.specialtyNameSelected ?? scheduledBooking.specialtyName,
			bookingNumber: scheduledBooking.bookingNumber,
			isRebooking,
			isScheduleScript: false,
			districtName: undefined,
			citiesNames: undefined,
			hasGeoDistrictsFilter: false,
			unitCode: isReplaceAction
				? ''
				: getFlowBasedValue(TELEMEDICINE_INFO.code, prev.unitCode),
			unitName: isReplaceAction
				? ''
				: getFlowBasedValue(TELEMEDICINE_INFO.name, prev.unitName),
			bookingType: isReplaceAction
				? BookingTypeEnum.HEALTH
				: getFlowBasedValue(BookingTypeEnum.TELEHEALTH, prev.bookingType),
		}));

		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.SEARCH_QUERY);
			return prev;
		});

		if (swapSpecialtyCallback) {
			swapSpecialtyCallback();
		}
	};

	const handleClose = () => {
		if (isScheduleScript) {
			navigate(HomeEnum.HOME_PAGE);
		}
		if (setOpen) {
			setOpen(false);
		}
	};

	const alertModalPaperProps = {
		maxWidth: isMobile ? '100%' : 450,
		borderRadius: isMobile ? 0 : 8,
		margin: isMobile ? 0 : 16,
		backgroundColor: isMobile ? colors.gray['20Percent'] : undefined,
	};

	return {
		handleSwapSpecialty,
		colors,
		isMobile,
		disabledSwapSpecialtyButton,
		handleClose,
		alertModalPaperProps,
		contentTitle,
		contentDescription,
		getSecondaryText,
		shouldProceedWithTelehealth,
		actionSelected,
	};
}

export {useHealthDuplicityDialog};
